import { Component, ElementRef, EventEmitter, forwardRef, Input, Output, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'app-email-address-list-control',
  templateUrl: './email-address-list-control.component.html',
  styleUrls: ['./email-address-list-control.component.scss'],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => EmailAddressListControlComponent),
    multi: true
  }]
})
export class EmailAddressListControlComponent implements ControlValueAccessor {
  @ViewChild('emailInput') emailInput: ElementRef<HTMLInputElement>;

  @Input() disabled = false;
  @Input() inputId: string;
  @Input() label = 'Email Addresses';
  @Input() placeholder = 'Add email...';
  @Input() required = false;
  @Input() showLabel = true;

  @Output() emailsChange = new EventEmitter<string[]>();

  emails: string[] = [];
  inputValue = '';
  totalLabel = '0 emails total';
  validationMessage = '';

  private touched = false;
  private readonly emailRegEx = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,50})+$/;

  private onChange: (value: string[]) => void = () => null;
  private onTouched: () => void = () => null;

  writeValue(value: string[]): void {
    this.emails = this.normalizeEmails(value || []);
    this.updateTotalLabel();
    this.updateRequiredValidationMessage();
  }

  registerOnChange(fn: (value: string[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  focusInput(): void {
    if (!this.disabled && this.emailInput) {
      this.emailInput.nativeElement.focus();
    }
  }

  inputChanged(value: string): void {
    this.inputValue = value;
    this.validationMessage = '';

    if (this.hasDelimiter(value)) {
      this.addEmailsFromText(value);
    }
  }

  inputKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === 'Tab' || event.key === ',' || event.key === ';') {
      if (this.inputValue.trim()) {
        event.preventDefault();
        this.addEmailsFromText(this.inputValue);
      }
      return;
    }

    if (event.key === 'Backspace' && !this.inputValue && this.emails.length) {
      this.removeEmail(this.emails[this.emails.length - 1], event);
    }
  }

  inputBlur(): void {
    this.touch();

    if (this.inputValue.trim()) {
      this.addEmailsFromText(this.inputValue);
      return;
    }

    this.updateRequiredValidationMessage();
  }

  inputPaste(event: ClipboardEvent): void {
    const text = event.clipboardData?.getData('text');

    if (!text || !this.hasDelimiter(text)) {
      return;
    }

    event.preventDefault();
    this.addEmailsFromText(text);
  }

  removeEmail(email: string, event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    this.emails = this.emails.filter(item => item.toLowerCase() !== email.toLowerCase());
    this.emitValue();
  }

  trackEmail(index: number, email: string): string {
    return email;
  }

  private addEmailsFromText(text: string): void {
    const parts = (text || '')
      .split(/[\s,;]+/)
      .map(email => email.trim())
      .filter(email => !!email);
    const validEmails = parts.filter(email => this.emailRegEx.test(email));
    const invalidEmails = parts.filter(email => !this.emailRegEx.test(email));

    if (validEmails.length) {
      this.emails = this.normalizeEmails([...this.emails, ...validEmails]);
      this.inputValue = '';
      this.emitValue();
    }

    this.validationMessage = invalidEmails.length ? `Invalid email: ${invalidEmails[0]}` : '';

    if (!invalidEmails.length) {
      this.updateRequiredValidationMessage();
    }
  }

  private emitValue(): void {
    this.touch();
    this.updateTotalLabel();
    this.updateRequiredValidationMessage();
    this.onChange([...this.emails]);
    this.emailsChange.emit([...this.emails]);
  }

  private normalizeEmails(emails: string[]): string[] {
    const normalizedEmails: string[] = [];

    for (const email of emails) {
      const normalizedEmail = (email || '').trim();

      if (!normalizedEmail || !this.emailRegEx.test(normalizedEmail)) {
        continue;
      }

      if (!normalizedEmails.some(item => item.toLowerCase() === normalizedEmail.toLowerCase())) {
        normalizedEmails.push(normalizedEmail);
      }
    }

    return normalizedEmails;
  }

  private hasDelimiter(value: string): boolean {
    return /[\s,;]/.test(value || '');
  }

  private updateTotalLabel(): void {
    const count = this.emails.length;
    this.totalLabel = `${count} ${count === 1 ? 'email' : 'emails'} total`;
  }

  private updateRequiredValidationMessage(): void {
    if (this.required && this.touched && !this.emails.length) {
      this.validationMessage = 'At least one email is required.';
      return;
    }

    if (this.validationMessage === 'At least one email is required.') {
      this.validationMessage = '';
    }
  }

  private touch(): void {
    if (this.touched) {
      return;
    }

    this.touched = true;
    this.onTouched();
  }
}
